import React, { useState, useEffect, useCallback } from 'react';
import { SharedContent, shareService } from '../../services/shareService';
import TeamsAnalyticsTable, { StatMatrixField } from '../../components/shared/TeamsAnalyticsTable';
import { API_URLS } from '../../config/Constants';

interface SharedTeamsReportProps {
  shareData: SharedContent;
}

const SharedTeamsReport: React.FC<SharedTeamsReportProps> = ({ shareData }) => {
  const [columns, setColumns] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [kpiTargets, setKpiTargets] = useState<any | null>(null);
  const [gameCount, setGameCount] = useState<number>(1); // Default to 1 for single game view

  // KPI helpers
  type KPIColor = 'green' | 'yellow' | 'orange' | 'red' | 'none';

  const getKpiColor = useCallback(
    (current: number | string, target: number | string, isInverse: boolean = false): KPIColor => {
      const curr = typeof current === 'string' ? parseFloat(current) : current;
      const targ = typeof target === 'string' ? parseFloat(target) : target;

      if (isNaN(curr) || isNaN(targ) || targ === 0) return 'none';

      const ratio = isInverse ? targ / curr : curr / targ;
      if (ratio >= 1.0) return 'green';
      if (ratio >= 0.85) return 'yellow';
      if (ratio >= 0.8) return 'orange';
      return 'red';
    },
    [],
  );

  const fetchKpiTargets = useCallback(async () => {
    try {
      const response = await shareService.publicFetch(
        `${API_URLS.BASE_URL}/share/seasons/kpi-targets?clientId=${shareData.clientId}&seasonId=${shareData.seasonId}`,
      );

      if (response.data.success) {
        setKpiTargets(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching KPI targets:', error);
    }
  }, [shareData.clientId, shareData.seasonId]);

  // Helper function to apply KPI colors to columns
  const applyKpiColorsToColumns = useCallback(
    (cols: any[], targets: any | null) => {
      if (!targets) return cols;

      const copy = cols.map((c) => ({ ...c }));
      const labelsToColor = new Set(['Q1', 'Q2', 'Q3', 'Q4', 'H1', 'H2', 'OT', 'Totals', 'Per Game']);

      // Helper to parse numbers safely (matches original)
      const parseNum = (s: any) => {
        if (typeof s === 'number') return s;
        const str = String(s).replace(/%|s/g, '').trim();
        const n = Number(str);
        return isNaN(n) ? NaN : n;
      };

      for (const col of copy) {
        if (!labelsToColor.has(col.label)) continue;

        try {
          // Determine scaling factors based on column type (simplified for shared page)
          const isPerGame = col.label === 'Per Game';
          const isTotals = col.label === 'Totals';

          // Calculate actual periods per game based on available periods (matching original logic)
          // Exclude "Totals" and "Per Game" from the count
          const actualPeriodsPerGame = copy.filter((c) => !['Totals', 'Per Game'].includes(c.label)).length;

          // Possession
          if (targets.possession) {
            // Possession % is always a percentage - no scaling needed
            const p = parseNum(col.percentUs);
            if (isFinite(p)) {
              col['percentUsKpiColor'] = getKpiColor(p, targets.possession.possessionPercent);
              col['percentUsKpiTarget'] = `${targets.possession.possessionPercent}%`;
            }

            // Ground balls % calculation
            const usgb = parseNum(col.gbUs);
            const themgb = parseNum(col.gbThem);
            if (isFinite(usgb) && isFinite(themgb) && usgb + themgb > 0) {
              col['gbUsKpiColor'] = getKpiColor((usgb / (usgb + themgb)) * 100, targets.possession.groundBallsPP);
              col['gbUsKpiTarget'] = `${targets.possession.groundBallsPP}%`;
            }

            // Turnovers - scale based on column type
            const to = parseNum(col.toUs);
            if (isFinite(to)) {
              let turnoverTarget = targets.possession.maxTurnoversPP;
              if (isPerGame) {
                turnoverTarget = targets.possession.maxTurnoversPP * actualPeriodsPerGame;
              } else if (isTotals) {
                turnoverTarget = targets.possession.maxTurnoversPP * actualPeriodsPerGame * gameCount;
              } else {
                turnoverTarget = targets.possession.maxTurnoversPP * gameCount;
              }
              col['toUsKpiColor'] = getKpiColor(to, turnoverTarget, true);
              col['toUsKpiTarget'] = turnoverTarget.toString();
            }
          }

          // Offense
          if (targets.offense) {
            // Shots - scale based on column type
            const shots = parseNum(col.shotsUs);
            if (isFinite(shots)) {
              let shotsTarget = targets.offense.minShotsPP;
              if (isPerGame) {
                shotsTarget = targets.offense.minShotsPP * actualPeriodsPerGame;
              } else if (isTotals) {
                shotsTarget = targets.offense.minShotsPP * actualPeriodsPerGame * gameCount;
              } else {
                shotsTarget = targets.offense.minShotsPP * gameCount;
              }
              col['shotsUsKpiColor'] = getKpiColor(shots, shotsTarget);
              col['shotsUsKpiTarget'] = shotsTarget.toString();
            }

            // Goals - scale based on column type
            const goals = parseNum(col.goalsUs);
            if (isFinite(goals)) {
              let goalsTarget = targets.offense.minGoalsPP;
              if (isPerGame) {
                goalsTarget = targets.offense.minGoalsPP * actualPeriodsPerGame;
              } else if (isTotals) {
                goalsTarget = targets.offense.minGoalsPP * actualPeriodsPerGame * gameCount;
              } else {
                goalsTarget = targets.offense.minGoalsPP * gameCount;
              }
              col['goalsUsKpiColor'] = getKpiColor(goals, goalsTarget);
              col['goalsUsKpiTarget'] = goalsTarget.toString();
            }

            // Shot % is always a percentage - no scaling needed
            const shotPct = parseNum(col.shotPctUs);
            if (isFinite(shotPct)) {
              col['shotPctUsKpiColor'] = getKpiColor(shotPct, targets.offense.shotPercent);
              col['shotPctUsKpiTarget'] = `${targets.offense.shotPercent}%`;
            }

            // EMOS % is always a percentage - no scaling needed
            const emosPct = parseNum(col.emosPctUs);
            if (isFinite(emosPct)) {
              col['emosPctUsKpiColor'] = getKpiColor(emosPct, targets.offense.emosPercent);
              col['emosPctUsKpiTarget'] = `${targets.offense.emosPercent}%`;
            }
          }

          // Defense
          if (targets.defense) {
            // Shots Allowed - scale based on column type
            const shotsAllowed = parseNum(col.shotsAllowedUs);
            if (isFinite(shotsAllowed)) {
              let shotsAllowedTarget = targets.defense.maxShotsAPP;
              if (isPerGame) {
                shotsAllowedTarget = targets.defense.maxShotsAPP * actualPeriodsPerGame;
              } else if (isTotals) {
                shotsAllowedTarget = targets.defense.maxShotsAPP * actualPeriodsPerGame * gameCount;
              } else {
                shotsAllowedTarget = targets.defense.maxShotsAPP * gameCount;
              }
              col['shotsAllowedUsKpiColor'] = getKpiColor(shotsAllowed, shotsAllowedTarget, true);
              col['shotsAllowedUsKpiTarget'] = shotsAllowedTarget.toString();
            }

            // MDDS % is always a percentage - no scaling needed
            const mddsPct = parseNum(col.mddsPctUs);
            if (isFinite(mddsPct)) {
              col['mddsPctUsKpiColor'] = getKpiColor(mddsPct, targets.defense.mddsPercent);
              col['mddsPctUsKpiTarget'] = `${targets.defense.mddsPercent}%`;
            }

            // Save % is always a percentage - no scaling needed
            const savePct = parseNum(col.savePctUs);
            if (isFinite(savePct)) {
              col['savePctUsKpiColor'] = getKpiColor(savePct, targets.defense.savePercent);
              col['savePctUsKpiTarget'] = `${targets.defense.savePercent}%`;
            }
          }

          // Clears / Rides
          if (targets.clearsRides) {
            // Clear % is always a percentage - no scaling needed
            const clearPct = parseNum(col.clearPctUs);
            if (isFinite(clearPct)) {
              col['clearPctUsKpiColor'] = getKpiColor(clearPct, targets.clearsRides.clearPercent);
              col['clearPctUsKpiTarget'] = `${targets.clearsRides.clearPercent}%`;
            }

            // Ride Wins - scale based on column type
            const rideWins = parseNum(col.rideWinsUs);
            if (isFinite(rideWins)) {
              let rideWinsTarget = targets.clearsRides.minRidesPP;
              if (isPerGame) {
                rideWinsTarget = targets.clearsRides.minRidesPP * actualPeriodsPerGame;
              } else if (isTotals) {
                rideWinsTarget = targets.clearsRides.minRidesPP * actualPeriodsPerGame * gameCount;
              } else {
                rideWinsTarget = targets.clearsRides.minRidesPP * gameCount;
              }
              col['rideWinsUsKpiColor'] = getKpiColor(rideWins, rideWinsTarget);
              col['rideWinsUsKpiTarget'] = rideWinsTarget.toString();
            }
          }

          // Faceoffs
          if (targets.faceoffs) {
            // Faceoff % is always a percentage - no scaling needed
            const facePct = parseNum(col.faceoffPctUs);
            if (isFinite(facePct)) {
              col['faceoffPctUsKpiColor'] = getKpiColor(facePct, targets.faceoffs.faceoffPercent);
              col['faceoffPctUsKpiTarget'] = `${targets.faceoffs.faceoffPercent}%`;
            }
          }
        } catch (error) {
          console.error('Error applying KPI colors:', error);
        }
      }

      return copy;
    },
    [getKpiColor, gameCount],
  );

  const mapStatsToMatrixColumn = useCallback((us: any, them: any) => {
    const formatTime = (seconds?: number): string => {
      if (typeof seconds !== 'number' || isNaN(seconds)) return '-';
      const mins = Math.floor(seconds / 60);
      const secs = Math.round(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatPercent = (value?: number): string => {
      if (typeof value !== 'number' || isNaN(value)) return '-';
      return `${Math.round(value)}%`;
    };

    return {
      // Possession metrics
      posTimeUs: us.possessionTime !== undefined ? formatTime(Number(us.possessionTime)) : '0:00',
      posTimeThem: them.possessionTime !== undefined ? formatTime(Number(them.possessionTime)) : '0:00',
      percentUs: formatPercent(us.possessionPercent),
      percentThem: formatPercent(them.possessionPercent),
      countUs: us.possessionCount ?? '-',
      countThem: them.possessionCount ?? '-',
      us: us.avgTimePerPossession !== undefined ? formatTime(Number(us.avgTimePerPossession)) : '-',
      them: them.avgTimePerPossession !== undefined ? formatTime(Number(them.avgTimePerPossession)) : '-',
      possessionTimeUs: formatTime(us.possessionTime),
      possessionTimeThem: formatTime(them.possessionTime),
      gbUs: us.groundballs ?? '-',
      gbThem: them.groundballs ?? '-',
      ctUs: us.causedturnover ?? '-',
      ctThem: them.causedturnover ?? '-',
      toUs: us.turnovers ?? '-',
      toThem: them.turnovers ?? '-',
      efficiencyUs: formatPercent(us.possessionEfficiency),
      efficiencyThem: formatPercent(them.possessionEfficiency),
      shotClockViolationsUs: us.shotClockViolations ?? '-',
      shotClockViolationsThem: them.shotClockViolations ?? '-',

      // Offense metrics
      shotsUs: us.shots ?? '-',
      shotsThem: them.shots ?? '-',
      goalsUs: us.goals ?? '-',
      goalsThem: them.goals ?? '-',
      shotPctUs: formatPercent(us.shotPercent),
      shotPctThem: formatPercent(them.shotPercent),
      shotPercentageUs: formatPercent(us.shotPercent),
      shotPercentageThem: formatPercent(them.shotPercent),
      sogPctUs: formatPercent(us.sogPercent),
      sogPctThem: formatPercent(them.sogPercent),
      emosUs: us.emos ?? '-',
      emosThem: them.emos ?? '-',
      emofUs: us.emof ?? '-',
      emofThem: them.emof ?? '-',
      emosPctUs: formatPercent(us.emosPercent),
      emosPctThem: formatPercent(them.emosPercent),

      // Defense metrics
      shotsAllowedUs: us.shotsallowed ?? '-',
      shotsAllowedThem: them.shotsallowed ?? '-',
      mddsUs: us.mdds ?? '-',
      mddsThem: them.mdds ?? '-',
      mddfUs: us.mddf ?? '-',
      mddfThem: them.mddf ?? '-',
      mddsPctUs: formatPercent(us.mddsPercent),
      mddsPctThem: formatPercent(them.mddsPercent),
      blockedUs: us.blocked ?? '-',
      blockedThem: them.blocked ?? '-',
      shotsOnGoalUs: us.shotsongoal ?? '-',
      shotsOnGoalThem: them.shotsongoal ?? '-',
      savesUs: us.saved ?? '-',
      savesThem: them.saved ?? '-',
      savePctUs: formatPercent(us.savePercent),
      savePctThem: formatPercent(them.savePercent),
      clearAttemptsUs: us.clearattempts ?? '-',
      clearAttemptsThem: them.clearattempts ?? '-',
      clearsUs: us.clears ?? '-',
      clearsThem: them.clears ?? '-',
      clearPctUs: formatPercent(us.clearPercent),
      clearPctThem: formatPercent(them.clearPercent),
      rideWinsUs: us.ridewins ?? '-',
      rideWinsThem: them.ridewins ?? '-',

      // Faceoff metrics
      faceoffWinsUs: us.faceoffwins ?? '-',
      faceoffWinsThem: them.faceoffwins ?? '-',
      faceoffLossesUs: us.faceofflosses ?? '-',
      faceoffLossesThem: them.faceofflosses ?? '-',
      faceoffPctUs: formatPercent(us.faceoffPercent),
      faceoffPctThem: formatPercent(them.faceoffPercent),

      // Legacy field names for backwards compatibility
      goals: us.goals || 0,
      goalsThey: them.goals || 0,
      shots: us.shots || 0,
      shotsThey: them.shots || 0,
      groundBalls: us.groundBalls || 0,
      groundBallsThey: them.groundBalls || 0,
      turnovers: us.turnovers || 0,
      turnoversThey: them.turnovers || 0,
      faceOffWins: us.faceoffwins || 0,
      faceOffWinsThey: them.faceoffwins || 0,
      penalties: us.penalties || 0,
      penaltiesThey: them.penalties || 0,
      possessionTime: formatTime(us.possessionTime),
      possessionTimeThey: formatTime(them.possessionTime),
      saves: us.saved || 0,
      savesThey: them.saved || 0,
      clears: us.clears || 0,
      clearsThey: them.clears || 0,
      clearAttempts: us.clearattempts || 0,
      clearAttemptsThey: them.clearattempts || 0,
    };
  }, []);

  // Helper function to transform backend data to frontend columns
  const transformStatSeriesToColumns = useCallback(
    (statSeriesData: any) => {
      const columns: any[] = [];

      if (!statSeriesData || typeof statSeriesData !== 'object') {
        return columns;
      }

      const { periods, stats, perGame } = statSeriesData;

      if (!periods || !stats) {
        return columns;
      }

      // Transform each period's stats
      periods.forEach((period: string) => {
        const periodStats = stats[period];
        if (periodStats) {
          columns.push({
            label: period,
            ...mapStatsToMatrixColumn(periodStats.us, periodStats.them),
          });
        }
      });

      // Add Per Game column if available (for season view)
      if (perGame && shareData.contentType === 'season') {
        const perGameColumn = {
          label: 'Per Game',
          ...mapStatsToMatrixColumn(perGame.us, perGame.them),
        };
        columns.push(perGameColumn);
      }

      return columns;
    },
    [mapStatsToMatrixColumn, shareData.contentType],
  );

  // Re-apply KPI colors when targets load/update or gameCount changes
  useEffect(() => {
    if (kpiTargets && columns.length > 0) {
      // Always re-apply when gameCount changes to ensure correct scaling
      const columnsWithKpi = applyKpiColorsToColumns(columns, kpiTargets);
      setColumns(columnsWithKpi);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyKpiColorsToColumns, kpiTargets, gameCount]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      let url = '';
      if (shareData.contentType === 'season') {
        url = `${API_URLS.BASE_URL}/share/seasons/stat-series?clientId=${shareData.clientId}&seasonId=${shareData.seasonId}`;
      } else {
        url = `${API_URLS.BASE_URL}/share/seasons/stat-series?clientId=${shareData.clientId}&seasonId=${shareData.seasonId}&gameId=${shareData.gameId}`;
      }

      const response = await shareService.publicFetch(url);

      if (response.data.success) {
        const statSeriesData = response.data.data;
        const transformedColumns = transformStatSeriesToColumns(statSeriesData);

        // Extract gameCount for KPI scaling
        if (statSeriesData.gameCount) {
          setGameCount(statSeriesData.gameCount);
        }

        // Don't apply KPI colors here - let useEffect handle it after targets load
        setColumns(transformedColumns);
      }
    } catch (error) {
      console.error('Error fetching teams data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [shareData.clientId, shareData.contentType, shareData.seasonId, shareData.gameId, transformStatSeriesToColumns]);

  useEffect(() => {
    fetchData();
    if (shareData.contentType === 'season') {
      fetchKpiTargets();
    }
  }, [fetchData, fetchKpiTargets, shareData]);

  // Define stat fields - matching exact order from original SeasonTeamsPage
  const getPossessionFields = (): StatMatrixField[] => [
    {
      label: 'Possession Time',
      key: 'posTimeUs',
      themKey: 'posTimeThem',
    },
    {
      label: 'Possession %',
      key: 'percentUs',
      themKey: 'percentThem',
    },
    {
      label: 'Possession Count',
      key: 'countUs',
      themKey: 'countThem',
    },
    { label: 'Avg. Time/Poss.', key: 'us', themKey: 'them' },
    {
      label: 'Poss. Efficiency',
      key: 'efficiencyUs',
      themKey: 'efficiencyThem',
    },
    { label: 'Ground Balls', key: 'gbUs', themKey: 'gbThem' },
    { label: 'Caused Turnovers', key: 'ctUs', themKey: 'ctThem' },
    { label: 'Turnovers', key: 'toUs', themKey: 'toThem' },
  ];

  const offenseFields: StatMatrixField[] = [
    { label: 'Shots', key: 'shotsUs', themKey: 'shotsThem' },
    { label: 'Goals', key: 'goalsUs', themKey: 'goalsThem' },
    { label: 'Shot %', key: 'shotPctUs', themKey: 'shotPctThem' },
    { label: 'SOG%', key: 'sogPctUs', themKey: 'sogPctThem' },
    { label: 'EMOS', key: 'emosUs', themKey: 'emosThem' },
    { label: 'EMOF', key: 'emofUs', themKey: 'emofThem' },
    { label: 'EMOS %', key: 'emosPctUs', themKey: 'emosPctThem' },
  ];

  const defenseFields: StatMatrixField[] = [
    {
      label: 'Shots Allowed',
      key: 'shotsAllowedUs',
      themKey: 'shotsAllowedThem',
    },
    { label: 'MDDS', key: 'mddsUs', themKey: 'mddsThem' },
    { label: 'MDDF', key: 'mddfUs', themKey: 'mddfThem' },
    { label: 'MDDS %', key: 'mddsPctUs', themKey: 'mddsPctThem' },
    { label: 'Blocked Shots', key: 'blockedUs', themKey: 'blockedThem' },
  ];

  const goalieFields: StatMatrixField[] = [
    {
      label: 'Shots on Goal',
      key: 'shotsOnGoalUs',
      themKey: 'shotsOnGoalThem',
    },
    { label: 'Saves', key: 'savesUs', themKey: 'savesThem' },
    { label: 'Save %', key: 'savePctUs', themKey: 'savePctThem' },
  ];

  const clearsRidesFields: StatMatrixField[] = [
    {
      label: 'Clear Attempts',
      key: 'clearAttemptsUs',
      themKey: 'clearAttemptsThem',
    },
    { label: 'Clears', key: 'clearsUs', themKey: 'clearsThem' },
    { label: 'Clear %', key: 'clearPctUs', themKey: 'clearPctThem' },
    { label: 'Ride Wins', key: 'rideWinsUs', themKey: 'rideWinsThem' },
  ];

  const faceoffsFields: StatMatrixField[] = [
    {
      label: 'Face-off Wins',
      key: 'faceoffWinsUs',
      themKey: 'faceoffWinsThem',
    },
    {
      label: 'Face-off Losses',
      key: 'faceoffLossesUs',
      themKey: 'faceoffLossesThem',
    },
    { label: 'Face-off %', key: 'faceoffPctUs', themKey: 'faceoffPctThem' },
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#05396b] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading analytics...</p>
          </div>
        ) : (
          <div className="flex flex-col gap-6 md:gap-8 w-full px-0 md:px-0">
            {/* Possession Section */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 px-4 md:px-0">Possession</h2>
              <div className="-mx-4 md:mx-0 overflow-x-auto md:overflow-visible">
                <div className="inline-block md:block md:w-full align-top rounded-none md:rounded-xl">
                  <TeamsAnalyticsTable fields={getPossessionFields()} columns={columns} kpiEnabled={!!kpiTargets} />
                </div>
              </div>
            </div>

            {/* Offense Section */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 px-4 md:px-0">Offense</h2>
              <div className="-mx-4 md:mx-0 overflow-x-auto md:overflow-visible">
                <div className="inline-block md:block md:w-full align-top rounded-none md:rounded-xl">
                  <TeamsAnalyticsTable fields={offenseFields} columns={columns} kpiEnabled={!!kpiTargets} />
                </div>
              </div>
            </div>

            {/* Defense Section */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 px-4 md:px-0">Defense</h2>
              <div className="-mx-4 md:mx-0 overflow-x-auto md:overflow-visible">
                <div className="inline-block md:block md:w-full align-top rounded-none md:rounded-xl">
                  <TeamsAnalyticsTable fields={defenseFields} columns={columns} kpiEnabled={!!kpiTargets} />
                </div>
              </div>
            </div>

            {/* Goalie Section */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 px-4 md:px-0">Goalie</h2>
              <div className="-mx-4 md:mx-0 overflow-x-auto md:overflow-visible">
                <div className="inline-block md:block md:w-full align-top rounded-none md:rounded-xl">
                  <TeamsAnalyticsTable fields={goalieFields} columns={columns} kpiEnabled={!!kpiTargets} />
                </div>
              </div>
            </div>

            {/* Clears & Rides Section */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 px-4 md:px-0">Clears & Rides</h2>
              <div className="-mx-4 md:mx-0 overflow-x-auto md:overflow-visible">
                <div className="inline-block md:block md:w-full align-top rounded-none md:rounded-xl">
                  <TeamsAnalyticsTable fields={clearsRidesFields} columns={columns} kpiEnabled={!!kpiTargets} />
                </div>
              </div>
            </div>

            {/* Face-offs Section */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 px-4 md:px-0">Face-offs</h2>
              <div className="-mx-4 md:mx-0 overflow-x-auto md:overflow-visible">
                <div className="inline-block md:block md:w-full align-top rounded-none md:rounded-xl">
                  <TeamsAnalyticsTable fields={faceoffsFields} columns={columns} kpiEnabled={!!kpiTargets} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-8 border-t border-gray-200 mt-12">
          <p className="text-sm text-gray-500">
            This is a read-only view. Data is powered by{' '}
            <a
              href="https://www.statdog.live"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#05396b] hover:text-[#0a5490] font-semibold underline"
            >
              STATDOG
            </a>
            .
          </p>
          <p className="text-xs text-gray-400 mt-2">Copyright © 2026 Sports Data Partners, LLC dba STATDOG</p>
          <p className="text-xs text-gray-400 mt-1">Access Count: {shareData.accessCount}</p>
        </div>
      </div>
    </div>
  );
};

export default SharedTeamsReport;
